import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Play, Stop, Pause, ArrowClockwise, MapPin, Microphone, Car, Clock } from "@phosphor-icons/react";
import { Button, IconButton } from "@/components/ui-kit/Button";
import { fmtYmdTime } from "@/lib/dateFormat";
import QrScanner from "@/components/QrScanner";
import { QrCode as QrCodeIcon } from "@phosphor-icons/react";

/**
 * StaffLiveTrip — 2-step ride flow for the staff role.
 *
 *   No active trip  → 大きな「乗車開始」ボタン → <StartTripDialog>
 *   Active trip     → ライブカード（経過時間タイマー / 一時停止 / 再開 / 終了）
 *
 * Pulls /api/operations/active on mount and every 30s while open. Posts to
 * /start, /pause, /resume, /finish. Falls back to safe error messages if GPS
 * permission is denied.
 */

export default function StaffLiveTrip({ vehicles, locations, kinds, onTripFinished }) {
    const [active, setActive] = useState(null); // null | doc
    const [busy, setBusy] = useState(false);
    const [startOpen, setStartOpen] = useState(false);
    const [preselectedVehicleId, setPreselectedVehicleId] = useState("");
    const [qrScanOpen, setQrScanOpen] = useState(false);
    const [finishOpen, setFinishOpen] = useState(false);
    const [pollTick, setPollTick] = useState(0);

    // 30s poll keeps the active timer fresh if user multitasks across tabs
    useEffect(() => {
        const t = setInterval(() => setPollTick((x) => x + 1), 30_000);
        return () => clearInterval(t);
    }, []);

    const loadActive = async () => {
        try {
            const { data } = await api.get("/operations/active");
            setActive(data);
        } catch (e) {
            // 401/403 propagate via api interceptor; ignore here
        }
    };
    useEffect(() => { loadActive(); }, [pollTick]);

    // Continuous GPS tracking — while a trip is in_progress (NOT paused),
    // capture coordinates every ~60s and POST to /gps-ping. The active
    // record's gps_track array grows server-side.
    useEffect(() => {
        if (!active || active.status !== "in_progress") return;
        if (!navigator.geolocation) return;
        let mounted = true;
        const sendPing = (pos) => {
            if (!mounted) return;
            api.post(`/operations/${active.id}/gps-ping`, {
                lat: pos.coords.latitude,
                lng: pos.coords.longitude,
                accuracy_m: pos.coords.accuracy,
                speed_kmh: pos.coords.speed != null ? pos.coords.speed * 3.6 : null,
            }).catch(() => { /* network drop — ignore, next tick will retry */ });
        };
        // Fire one immediately so the live feed has a fresh point right away
        navigator.geolocation.getCurrentPosition(sendPing, () => {}, { enableHighAccuracy: true, timeout: 15_000, maximumAge: 30_000 });
        const tick = setInterval(() => {
            navigator.geolocation.getCurrentPosition(sendPing, () => {}, { enableHighAccuracy: true, timeout: 15_000, maximumAge: 30_000 });
        }, 60_000);
        return () => { mounted = false; clearInterval(tick); };
    }, [active?.id, active?.status]);

    const containerRef = useRef(null);

    const startTrip = async (payload) => {
        setBusy(true);
        try {
            const { data } = await api.post("/operations/start", payload);
            setActive(data);
            setStartOpen(false);
            toast.success("🚖 乗車開始");
            // After the dialog closes, scroll the page back to the top so the
            // freshly-rendered ActiveTripCard (with the timer + 乗車終了 button)
            // is visible immediately. Mobile users were stuck halfway down
            // the operations list otherwise.
            requestAnimationFrame(() => {
                window.scrollTo({ top: 0, behavior: "smooth" });
                if (containerRef.current?.scrollIntoView) {
                    containerRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
                }
            });
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "開始失敗");
        } finally { setBusy(false); }
    };

    const pauseTrip = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/operations/${active.id}/pause`);
            setActive(data);
            toast.info("⏸ 一時停止しました");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "一時停止失敗");
        } finally { setBusy(false); }
    };

    const resumeTrip = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/operations/${active.id}/resume`);
            setActive(data);
            toast.success("▶ 運行を再開");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "再開失敗");
        } finally { setBusy(false); }
    };

    const finishTrip = async (payload) => {
        setBusy(true);
        try {
            const { data } = await api.post(`/operations/${active.id}/finish`, payload);
            setActive(null);
            setFinishOpen(false);
            toast.success(`🛑 乗車終了 ${data.distance_km != null ? `(${data.distance_km} km)` : ""}`);
            if (data._triggered_alerts?.length) {
                toast.warning(`⚠ ${data._triggered_alerts[0].title}`, { duration: 8000 });
            }
            if (onTripFinished) onTripFinished();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "終了失敗");
        } finally { setBusy(false); }
    };

    return (
        <div ref={containerRef} className="space-y-3" data-testid="staff-live-trip">
            {!active ? (
                <Button
                    onClick={() => setQrScanOpen(true)}
                    leftIcon={QrCodeIcon}
                    leftIconWeight="fill"
                    iconSize="lg"
                    className="w-full !py-6 !text-lg sm:!text-xl !font-bold active:scale-[0.99] transition !justify-center"
                    data-testid="start-trip-btn"
                >
                    乗車開始（QRスキャン）
                </Button>
            ) : (
                <ActiveTripCard
                    trip={active}
                    busy={busy}
                    onPause={pauseTrip}
                    onResume={resumeTrip}
                    onFinish={() => setFinishOpen(true)}
                />
            )}

            {startOpen && (
                <StartTripDialog
                    vehicles={vehicles}
                    locations={locations}
                    kinds={kinds}
                    busy={busy}
                    preselectedVehicleId={preselectedVehicleId}
                    onCancel={() => { setStartOpen(false); setPreselectedVehicleId(""); }}
                    onStart={startTrip}
                />
            )}
            {finishOpen && active && (
                <FinishTripDialog
                    trip={active}
                    locations={locations}
                    busy={busy}
                    onCancel={() => setFinishOpen(false)}
                    onFinish={finishTrip}
                />
            )}
            <QrScanner
                open={qrScanOpen}
                onClose={() => setQrScanOpen(false)}
                onDetect={async (code) => {
                    try {
                        const { data } = await api.post("/vehicles/resolve-qr", { code });
                        toast.success(`車両を選択: ${data.plate_number}`);
                        setPreselectedVehicleId(data.id);
                        setQrScanOpen(false);
                        setStartOpen(true);
                    } catch (e) {
                        toast.error(formatApiError(e.response?.data?.detail) || "QRコードを認識できません");
                    }
                }}
            />
        </div>
    );
}

// =================================================================
// Live timer card
// =================================================================
function ActiveTripCard({ trip, busy, onPause, onResume, onFinish }) {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, []);

    const startedMs = trip.started_at ? new Date(trip.started_at).getTime() : now;
    const pauseMs = (trip.pauses || []).reduce((acc, p) => {
        const pa = p.paused_at ? new Date(p.paused_at).getTime() : null;
        const rs = p.resumed_at ? new Date(p.resumed_at).getTime() : (trip.status === "paused" ? now : null);
        return pa && rs ? acc + (rs - pa) : acc;
    }, 0);
    const elapsedSec = Math.max(0, Math.floor((now - startedMs) / 1000));
    const activeSec = Math.max(0, Math.floor((now - startedMs - pauseMs) / 1000));
    const fmt = (s) => {
        const h = Math.floor(s / 3600);
        const m = Math.floor((s % 3600) / 60);
        const sec = s % 60;
        return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}` : `${m}:${String(sec).padStart(2, "0")}`;
    };
    const isPaused = trip.status === "paused";

    return (
        <div className={`prc-card p-4 sm:p-5 border-l-4 ${isPaused ? "border-l-amber-400" : "border-l-[var(--prc-green)]"}`} data-testid="active-trip-card">
            <div className="flex items-center justify-between mb-2">
                <p className={`prc-label-overline mb-0 ${isPaused ? "!text-amber-300" : "!text-[var(--prc-green)]"}`}>
                    {isPaused ? "// 一時停止中" : "// 進行中の運行"}
                </p>
                <span className="font-mono text-xs text-[var(--prc-muted)]">{fmtYmdTime(`${trip.operation_date}T${trip.start_time || "00:00"}`, `${trip.operation_date} ${trip.start_time}`)}〜</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <div>
                    <p className="prc-label-overline mb-0.5">// 経過時間</p>
                    <p className={`text-3xl sm:text-4xl font-bold font-mono ${isPaused ? "text-amber-300" : "text-[var(--prc-green)]"}`} data-testid="active-elapsed">
                        {fmt(elapsedSec)}
                    </p>
                    {pauseMs > 0 && <p className="text-[10px] text-[var(--prc-muted)] mt-0.5 font-mono">// 稼働 {fmt(activeSec)} ・休憩 {fmt(elapsedSec - activeSec)}</p>}
                </div>
                <div>
                    <p className="prc-label-overline mb-0.5">// 車両 / 出発</p>
                    <p className="text-base font-semibold truncate"><Car size={16} className="inline mr-1" />{trip.vehicle_plate}</p>
                    {trip.origin && <p className="text-xs text-[var(--prc-muted)] truncate"><MapPin size={16} className="inline mr-1" />{trip.origin}</p>}
                    {trip.start_odometer_km != null && <p className="text-xs text-[var(--prc-muted)] font-mono">// 開始 {trip.start_odometer_km.toLocaleString()} km</p>}
                </div>
            </div>

            <div className="grid grid-cols-2 gap-2 mt-3">
                {isPaused ? (
                    <Button
                        onClick={onResume}
                        disabled={busy}
                        leftIcon={ArrowClockwise}
                        leftIconWeight="bold"
                        className="!py-3 !justify-center"
                        data-testid="resume-trip-btn"
                    >再開</Button>
                ) : (
                    <Button
                        variant="ghost"
                        onClick={onPause}
                        disabled={busy}
                        leftIcon={Pause}
                        leftIconWeight="fill"
                        className="!py-3 !justify-center"
                        data-testid="pause-trip-btn"
                    >一時停止</Button>
                )}
                <Button
                    variant="danger"
                    onClick={onFinish}
                    disabled={busy}
                    leftIcon={Stop}
                    leftIconWeight="fill"
                    className="!py-3 !justify-center !text-base"
                    data-testid="finish-trip-btn"
                >乗車終了</Button>
            </div>
        </div>
    );
}

// =================================================================
// Start dialog
// =================================================================
function StartTripDialog({ vehicles, locations, kinds, busy, preselectedVehicleId, onCancel, onStart }) {
    const [vehicleId, setVehicleId] = useState(preselectedVehicleId || "");
    const [origin, setOrigin] = useState("");
    const [startOdo, setStartOdo] = useState("");
    const [purpose, setPurpose] = useState("");
    const [kind, setKind] = useState("business");
    const [passengers, setPassengers] = useState("");
    // GPS state machine: idle | requesting | granted | denied | unavailable | timeout
    const [gpsState, setGpsState] = useState("idle");
    const [gps, setGps] = useState(null);
    const [lastOdo, setLastOdo] = useState(null);

    // Auto-grab GPS on dialog mount. Diagnoses the precise reason for a
    // failure so staff can quickly tell if it's a permissions issue vs an
    // indoor signal vs a missing browser API.
    const grabGps = (silent = false) => {
        if (!navigator.geolocation) {
            setGpsState("unavailable");
            if (!silent) toast.error("この端末は位置情報に対応していません");
            return;
        }
        setGpsState("requesting");
        navigator.geolocation.getCurrentPosition(
            (p) => {
                setGps({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy_m: p.coords.accuracy });
                setGpsState("granted");
                if (!silent) toast.success(`GPS取得 (精度 ±${Math.round(p.coords.accuracy)}m)`);
            },
            (err) => {
                if (err.code === 1) { setGpsState("denied"); if (!silent) toast.error("位置情報の許可が必要です。端末の設定をご確認ください"); }
                else if (err.code === 2) { setGpsState("unavailable"); if (!silent) toast.error("位置情報を取得できません（屋内・トンネル？）"); }
                else if (err.code === 3) { setGpsState("timeout"); if (!silent) toast.error("位置情報の取得がタイムアウトしました"); }
                else { setGpsState("unavailable"); if (!silent) toast.error(`GPS取得失敗: ${err.message}`); }
            },
            { enableHighAccuracy: true, timeout: 15_000, maximumAge: 30_000 }
        );
    };

    // Auto-fire on mount once HTTPS check passes (browser will prompt for
    // permission the first time; subsequent opens reuse the cached grant).
    useEffect(() => { grabGps(true); /* eslint-disable-next-line */ }, []);

    // Pre-fill the start odometer from the previous trip on this vehicle.
    // Replaces the value when the user switches vehicles (we track the
    // previously-fetched auto-fill so we don't overwrite a manual entry).
    useEffect(() => {
        if (!vehicleId) { setLastOdo(null); return; }
        let cancel = false;
        const previousAutoFill = lastOdo?.last_end_odometer_km;
        (async () => {
            try {
                const { data } = await api.get(`/operations/last-odometer/${vehicleId}`);
                if (cancel) return;
                setLastOdo(data);
                const newOdo = data?.last_end_odometer_km;
                setStartOdo((cur) => {
                    const isEmpty = cur === "" || cur == null;
                    const isStaleAutoFill = previousAutoFill != null && String(cur) === String(previousAutoFill);
                    if (newOdo != null && (isEmpty || isStaleAutoFill)) return String(newOdo);
                    if (newOdo == null && isStaleAutoFill) return "";
                    return cur;
                });
            } catch (e) { /* ignore */ }
        })();
        return () => { cancel = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vehicleId]);

    // Voice input via the browser-native Web Speech API (free, no Whisper)
    const voiceTo = (setter) => {
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SR) { toast.error("音声入力非対応のブラウザです"); return; }
        const rec = new SR();
        rec.lang = "ja-JP";
        rec.interimResults = false;
        rec.maxAlternatives = 1;
        rec.onresult = (e) => setter(e.results[0][0].transcript);
        rec.onerror = (e) => toast.error(`音声入力エラー: ${e.error}`);
        rec.start();
        toast.info("🎤 話しかけてください");
    };

    const submit = (e) => {
        e?.preventDefault();
        if (!vehicleId) { toast.error("車両を選択してください"); return; }
        onStart({
            vehicle_id: vehicleId,
            origin: origin || null,
            start_odometer_km: startOdo ? parseInt(startOdo) : null,
            purpose: purpose || null,
            kind,
            passenger_count: passengers ? parseInt(passengers) : null,
            gps_start_lat: gps?.lat ?? null,
            gps_start_lng: gps?.lng ?? null,
        });
    };

    const veh = vehicles.find((v) => v.id === vehicleId);
    const seatCap = Math.max(1, Math.min(60, veh?.seating_capacity || 10));

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center sm:p-4">
            <form onSubmit={submit} className="prc-card op-form-scale p-4 sm:p-6 w-full sm:max-w-2xl max-h-[95vh] overflow-y-auto rounded-t-lg sm:rounded-sm" data-testid="start-trip-dialog">
                <p className="prc-label-overline mb-1 !text-[var(--prc-green)]">// 乗車開始</p>
                <h2 className="text-lg sm:text-xl font-bold mb-4">運行を開始しますか？</h2>

                <Field label="車両 *">
                    {(() => {
                        const v = vehicles.find((x) => x.id === vehicleId);
                        return (
                            <div className="prc-input !opacity-90 !cursor-default select-text" data-testid="start-vehicle-readonly">
                                {v ? (
                                    <span className="font-mono">
                                        <span className="font-bold text-[var(--prc-ink)]">{v.plate_number}</span>
                                        <span className="text-[var(--prc-muted)] ml-2">— {v.make} {v.model}</span>
                                    </span>
                                ) : (
                                    <span className="text-[var(--prc-tertiary)]">QRスキャンで車両を選択してください</span>
                                )}
                                <input
                                    type="text"
                                    required
                                    value={vehicleId}
                                    readOnly
                                    onChange={() => {}}
                                    aria-hidden="true"
                                    tabIndex={-1}
                                    style={{ position: "absolute", opacity: 0, pointerEvents: "none", height: 0, width: 0 }}
                                />
                            </div>
                        );
                    })()}
                </Field>

                <Field label="出発地">
                    <div className="flex gap-2 items-stretch">
                        <select className="prc-input flex-1" value={origin} onChange={(e) => setOrigin(e.target.value)} data-testid="start-origin">
                            <option value="">未選択</option>
                            {locations.map((l) => <option key={l.code} value={l.label}>{l.label}</option>)}
                        </select>
                        <button type="button" onClick={() => voiceTo(setOrigin)} className="prc-btn-ghost !px-3" title="音声入力">
                            <Microphone size={16} />
                        </button>
                        <button type="button" onClick={() => grabGps(false)} disabled={gpsState === "requesting"} className="prc-btn-ghost !px-3 disabled:opacity-50" title="GPS取得" data-testid="start-gps-btn">
                            <MapPin size={16} className={gps ? "text-[var(--prc-green)]" : gpsState === "denied" ? "text-rose-300" : ""} />
                        </button>
                    </div>
                    <GpsStatus state={gpsState} gps={gps} onRetry={() => grabGps(false)} />
                </Field>

                <Field label="開始 オドメータ (km)">
                    <input type="number" className="prc-input" value={startOdo} onChange={(e) => setStartOdo(e.target.value)} data-testid="start-odo" />
                    {lastOdo?.last_end_odometer_km != null && (
                        <p className="text-[10px] text-[var(--prc-muted)] mt-1 font-mono">
                            // 前回終了: {lastOdo.last_end_odometer_km.toLocaleString()} km ({lastOdo.last_date})
                        </p>
                    )}
                </Field>

                <Field label="区分">
                    <select className="prc-input" value={kind} onChange={(e) => setKind(e.target.value)}>
                        {kinds.map((k) => <option key={k.code} value={k.code}>{k.label}</option>)}
                    </select>
                </Field>

                <Field label="目的 / 取引先">
                    <div className="flex gap-2 items-stretch">
                        <input className="prc-input flex-1" value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder="例: 顧客訪問" data-testid="start-purpose" />
                        <button type="button" onClick={() => voiceTo(setPurpose)} className="prc-btn-ghost !px-3" title="音声入力"><Microphone size={16} /></button>
                    </div>
                </Field>

                <Field label="乗車人数">
                    <select className="prc-input" value={passengers} onChange={(e) => setPassengers(e.target.value)}>
                        <option value="">選択してください</option>
                        {Array.from({ length: seatCap }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n} 名</option>)}
                    </select>
                </Field>

                <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-[var(--prc-border)]">
                    <button type="button" onClick={onCancel} className="prc-btn-ghost">キャンセル</button>
                    <button type="submit" disabled={busy} className="prc-btn-primary disabled:opacity-50 inline-flex items-center gap-2" data-testid="start-trip-submit">
                        <Play size={16} weight="fill" />
                        {busy ? "開始中..." : "乗車開始"}
                    </button>
                </div>
            </form>
        </div>
    );
}

// =================================================================
// Finish dialog
// =================================================================
function FinishTripDialog({ trip, locations, busy, onCancel, onFinish }) {
    const [destination, setDestination] = useState("");
    const [endOdo, setEndOdo] = useState("");
    const [passengers, setPassengers] = useState(trip.passenger_count || "");
    const [notes, setNotes] = useState("");
    const [gpsState, setGpsState] = useState("idle");
    const [gps, setGps] = useState(null);
    const [confirming, setConfirming] = useState(false);
    const confirmPanelRef = useRef(null);

    // When we transition INTO the confirm step, scroll the modal back to the
    // top so the "この内容で登録しますか？" headline is visible immediately
    // — otherwise the user was left looking at the bottom of the form.
    useEffect(() => {
        if (confirming && confirmPanelRef.current) {
            confirmPanelRef.current.scrollTop = 0;
        }
    }, [confirming]);

    const distance = useMemo(() => {
        const e = parseInt(endOdo);
        const s = trip.start_odometer_km;
        if (!Number.isFinite(e) || s == null) return null;
        return Math.max(0, e - s);
    }, [endOdo, trip.start_odometer_km]);

    const grabGps = (silent = false) => {
        if (!navigator.geolocation) {
            setGpsState("unavailable");
            if (!silent) toast.error("この端末は位置情報に対応していません");
            return;
        }
        setGpsState("requesting");
        navigator.geolocation.getCurrentPosition(
            (p) => {
                setGps({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy_m: p.coords.accuracy });
                setGpsState("granted");
                if (!silent) toast.success(`GPS取得 (精度 ±${Math.round(p.coords.accuracy)}m)`);
            },
            (err) => {
                if (err.code === 1) { setGpsState("denied"); if (!silent) toast.error("位置情報の許可が必要です"); }
                else if (err.code === 2) { setGpsState("unavailable"); if (!silent) toast.error("位置情報を取得できません"); }
                else if (err.code === 3) { setGpsState("timeout"); if (!silent) toast.error("位置情報の取得がタイムアウトしました"); }
                else { setGpsState("unavailable"); if (!silent) toast.error(`GPS取得失敗: ${err.message}`); }
            },
            { enableHighAccuracy: true, timeout: 15_000, maximumAge: 30_000 }
        );
    };
    useEffect(() => { grabGps(true); /* eslint-disable-next-line */ }, []);

    const voiceTo = (setter) => {
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SR) { toast.error("音声入力非対応"); return; }
        const rec = new SR();
        rec.lang = "ja-JP"; rec.interimResults = false;
        rec.onresult = (e) => setter(e.results[0][0].transcript);
        rec.start();
        toast.info("🎤 話しかけてください");
    };

    const submit = () => {
        if (!confirming) { setConfirming(true); return; }
        onFinish({
            destination: destination || null,
            end_odometer_km: endOdo ? parseInt(endOdo) : null,
            passenger_count: passengers ? parseInt(passengers) : null,
            notes: notes || null,
            gps_end_lat: gps?.lat ?? null,
            gps_end_lng: gps?.lng ?? null,
        });
    };

    if (confirming) {
        return (
            <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center sm:p-4">
                <div ref={confirmPanelRef} className="prc-card p-3 sm:p-6 w-full sm:max-w-2xl max-h-[100dvh] sm:max-h-[90vh] overflow-y-auto rounded-t-lg sm:rounded-sm" data-testid="finish-confirm">
                    <p className="prc-label-overline mb-1 !text-amber-300 !text-[10px] sm:!text-xs">// 内容を確認してください</p>
                    <h2 className="text-base sm:text-2xl font-bold mb-3">この内容で運行を終了しますか？</h2>
                    <div className="grid grid-cols-2 gap-x-3">
                        <ConfirmRow label="到着地" value={destination || "—"} full />
                        <ConfirmRow label="開始→終了 オドメータ" value={`${trip.start_odometer_km || "—"} → ${endOdo || "—"} km`} full accent="font-mono text-[var(--prc-green)]" />
                        {distance != null && <ConfirmRow label="走行距離" value={`${distance.toLocaleString()} km`} accent="text-xl font-mono text-[var(--prc-green)]" />}
                        {passengers && <ConfirmRow label="乗車人数" value={`${passengers} 名`} />}
                        {notes && <ConfirmRow label="備考" value={notes} full />}
                    </div>
                    <div className="flex flex-row justify-end gap-2 mt-3 pt-3 border-t border-[var(--prc-border)]">
                        <button onClick={() => setConfirming(false)} className="prc-btn-ghost flex-1 sm:flex-initial">戻って修正</button>
                        <button onClick={submit} disabled={busy} className="py-3 px-4 rounded-sm font-bold bg-rose-600 hover:bg-rose-500 text-white flex-1 sm:flex-initial disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="finish-trip-confirm">
                            <Stop size={16} weight="fill" />
                            {busy ? "送信中..." : "✓ 乗車終了"}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center sm:p-4">
            <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="prc-card op-form-scale p-4 sm:p-6 w-full sm:max-w-2xl max-h-[95vh] overflow-y-auto rounded-t-lg sm:rounded-sm" data-testid="finish-trip-dialog">
                <p className="prc-label-overline mb-1 !text-rose-300">// 乗車終了</p>
                <h2 className="text-lg sm:text-xl font-bold mb-4">運行情報を入力してください</h2>

                <Field label="到着地">
                    <div className="flex gap-2 items-stretch">
                        <select className="prc-input flex-1" value={destination} onChange={(e) => setDestination(e.target.value)} data-testid="finish-destination">
                            <option value="">未選択</option>
                            {locations.map((l) => <option key={l.code} value={l.label}>{l.label}</option>)}
                        </select>
                        <button type="button" onClick={() => voiceTo(setDestination)} className="prc-btn-ghost !px-3"><Microphone size={16} /></button>
                        <button type="button" onClick={() => grabGps(false)} disabled={gpsState === "requesting"} className="prc-btn-ghost !px-3 disabled:opacity-50" data-testid="finish-gps-btn">
                            <MapPin size={16} className={gps ? "text-[var(--prc-green)]" : gpsState === "denied" ? "text-rose-300" : ""} />
                        </button>
                    </div>
                    <GpsStatus state={gpsState} gps={gps} onRetry={() => grabGps(false)} />
                </Field>

                <Field label="終了 オドメータ (km)">
                    <input type="number" className="prc-input" value={endOdo} onChange={(e) => setEndOdo(e.target.value)} data-testid="finish-end-odo" />
                    {distance != null && (
                        <p className="text-[10px] text-[var(--prc-green)] mt-1 font-mono">// 走行距離 {distance.toLocaleString()} km (開始 {trip.start_odometer_km})</p>
                    )}
                </Field>

                <Field label="乗車人数">
                    <input type="number" className="prc-input" value={passengers} onChange={(e) => setPassengers(e.target.value)} placeholder="任意" />
                </Field>

                <Field label="備考">
                    <div className="flex gap-2 items-stretch">
                        <textarea className="prc-input min-h-[60px] flex-1" value={notes} onChange={(e) => setNotes(e.target.value)} />
                        <button type="button" onClick={() => voiceTo(setNotes)} className="prc-btn-ghost !px-3 self-start"><Microphone size={16} /></button>
                    </div>
                </Field>

                <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-[var(--prc-border)]">
                    <button type="button" onClick={onCancel} className="prc-btn-ghost">キャンセル</button>
                    <button type="submit" disabled={busy} className="py-3 px-5 rounded-sm font-bold bg-rose-600 hover:bg-rose-500 text-white inline-flex items-center gap-2 disabled:opacity-50" data-testid="finish-trip-next">
                        <Clock size={16} />確認画面へ
                    </button>
                </div>
            </form>
        </div>
    );
}

// =================================================================
// Helpers
// =================================================================
function Field({ label, children }) {
    return (
        <div className="mb-3">
            <label className="prc-label-overline block mb-1">{label}</label>
            {children}
        </div>
    );
}

// Inline GPS status banner — visible right under the GPS button so staff
// understand exactly why coordinates didn't show up.
function GpsStatus({ state, gps, onRetry }) {
    if (state === "granted" && gps) {
        return (
            <p className="text-[10px] text-[var(--prc-green)] mt-1 font-mono" data-testid="gps-status-ok">
                // ✓ GPS: {gps.lat.toFixed(5)}, {gps.lng.toFixed(5)} (±{Math.round(gps.accuracy_m || 0)}m)
            </p>
        );
    }
    if (state === "requesting") {
        return <p className="text-[10px] text-amber-300 mt-1 font-mono">// 位置情報を取得中...</p>;
    }
    if (state === "denied") {
        return (
            <p className="text-[10px] text-rose-300 mt-1 font-mono">
                // ⚠ 位置情報の許可が必要です。<button type="button" onClick={onRetry} className="underline ml-1">再試行</button>
            </p>
        );
    }
    if (state === "timeout") {
        return (
            <p className="text-[10px] text-amber-300 mt-1 font-mono">
                // ⏱ 取得タイムアウト（屋内？）<button type="button" onClick={onRetry} className="underline ml-1">再試行</button>
            </p>
        );
    }
    if (state === "unavailable") {
        return (
            <p className="text-[10px] text-rose-300 mt-1 font-mono">
                // ⚠ 位置情報を取得できません。<button type="button" onClick={onRetry} className="underline ml-1">再試行</button>
            </p>
        );
    }
    return null;
}

function ConfirmRow({ label, value, accent, full }) {
    return (
        <div className={`py-2 border-b border-[var(--prc-border)] last:border-b-0 ${full ? "col-span-2" : ""}`}>
            <p className="prc-label-overline !text-[10px] sm:!text-xs mb-0.5">{label}</p>
            <p className={`text-lg sm:text-2xl font-semibold leading-tight break-words ${accent || ""}`}>
                {value == null || value === "" ? <span className="text-[var(--prc-muted)]">—</span> : value}
            </p>
        </div>
    );
}
